import { useEffect, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@stores/authStore';
import { useNotificationStore, type Notification } from '@stores/notificationStore';
import { getAccessToken } from '@/lib/tokenStorage';

/** How long the connection outlives its last holder (a page change remounts them). */
const RELEASE_GRACE_MS = 3_000;

/** Back-off between reconnect attempts: 1s doubling to this ceiling, for as long as it takes. */
const MAX_RECONNECT_DELAY_MS = 10_000;

// Map backend notification types to frontend display types
function mapNotificationType(backendType: string): Notification['type'] {
  const typeMapping: Record<string, Notification['type']> = {
    sla_breach: 'error',
    sla_warning: 'warning',
    ticket_assigned: 'info',
    ticket_updated: 'info',
    ticket_resolved: 'success',
    mention: 'info',
    // An approver is needed: shown as a toast as well as in the bell.
    ticket_access_requested: 'warning',
    ticket_access_decided: 'info',
    info: 'info',
    success: 'success',
    warning: 'warning',
    error: 'error',
  };
  return typeMapping[backendType] || 'info';
}

export interface WebSocketEvents {
  // Ticket events
  'ticket:created': {
    id: string;
    ticketNumber?: string;
    subject: string;
    status?: string;
    priority: string;
    channel?: string;
    stageId?: string;
    pipelineId?: string;
    customerId?: string;
    assigneeId?: string;
    tags?: string[];
    createdAt?: string;
    updatedAt?: string;
    [key: string]: any;
  };
  'ticket:updated': { ticketId: string; stageId?: string; previousStageId?: string; stageChanged?: boolean; [key: string]: any };
  'ticket:assigned': { ticketId: string; assigneeId?: string };

  // Collision detection (#18947): presence/typing of OTHER users on the same
  // ticket. The server rebroadcasts these to everyone else in the ticket room.
  'ticket:user_activity': { userId: string; email: string; activityType: string; context?: string; timestamp: string };
  'ticket:typing': { userId: string; email: string; isTyping: boolean; context?: string; timestamp?: string };
  'ticket:collision': { userId: string; userName: string; activityType: string; context?: string };
  'ticket:user_left': { userId: string; email: string; timestamp: string };

  // Message events
  'message:new': { ticketId: string; conversationId: string; message: any };

  // A customer message arrived on a social channel. A SIGNAL, not the message:
  // this goes to the whole tenant room, so the inbox refetches the conversation
  // through the authenticated API rather than rendering anything from here.
  'inbox:message': {
    channel: 'whatsapp' | 'facebook';
    conversationId: string;
    participantId: string;
    messageType: string;
    isNewConversation: boolean;
  };

  // Notification events
  notification: {
    id: string;
    type: string; // Backend sends types like 'sla_breach', 'ticket_assigned', etc.
    title: string;
    message: string;
    link?: string;
    data?: {
      ticketId?: string;
      ticketNumber?: string;
      breachType?: string;
      minutesPast?: number;
      severity?: string;
      recipientRole?: string;
      assigneeName?: string;
    };
    createdAt: string;
  };

  // SLA events
  'sla:warning': { ticketId: string; type: 'approaching' | 'breached' };

  // Live chat queue (BFF websocket.gateway notifyChatSession*, tenant room)
  'chat:session:new': { id?: string; [key: string]: any };
  'chat:session:assigned': { sessionId: string; agentId?: string };
  'chat:session:ended': { sessionId: string };

  // Typing events
  typing: { userId: string; isTyping: boolean };

  // Plan / subscription lifecycle (G8 + G29)
  'plan.upgraded': { tenantId: string; fromPlan: string; toPlan: string };
  'plan.downgraded': { tenantId: string; fromPlan: string; toPlan: string };
  'plan.cancelled': { tenantId: string; plan: string };
  'plan.change_requested': {
    tenantId: string;
    requestId: string;
    fromPlan: string;
    toPlan: string;
  };

  // Connection events
  connected: { userId: string; tenantId: string };
  error: { message: string };
}

type EventHandler<T> = (data: T) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private eventHandlers: Map<string, Set<EventHandler<any>>> = new Map();
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  /** The token the page started with; the current one is read on every (re)connect. */
  private initialToken: string | null = null;
  private serverDropTimer: ReturnType<typeof setTimeout> | null = null;
  private wakeListenersAdded = false;
  private holders = 0;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private notificationBridge: (() => void) | null = null;
  private connectionChangeListeners: Set<(connected: boolean) => void> = new Set();
  /**
   * Room-subscription messages (e.g. subscribe_voice_queue) that must hold for
   * the life of the page: sent as soon as the socket connects and sent again
   * after every reconnect, since a new socket starts in no rooms.
   */
  private standingSubscriptions: Map<string, { event: string; data: Record<string, unknown>; holders: number }> =
    new Map();

  onConnectionChange(listener: (connected: boolean) => void) {
    this.connectionChangeListeners.add(listener);
    return () => { this.connectionChangeListeners.delete(listener); };
  }

  private notifyConnectionChange(connected: boolean) {
    this.connectionChangeListeners.forEach((listener) => listener(connected));
  }

  connect(token: string) {
    // A socket that exists but has not connected yet is still ours: creating
    // another would leave two live sockets delivering every event twice.
    if (this.socket) {
      return;
    }

    const wsUrl = import.meta.env.VITE_WS_URL || '';
    this.initialToken = token;

    this.socket = io(`${wsUrl}/ws`, {
      // Read on EVERY connect and reconnect. Access tokens are short-lived and
      // refreshed in the background for API calls; a token fixed at page load
      // was rejected on the first reconnect after it expired, and live updates
      // stayed dead ("Connecting…") until a full reload.
      auth: (cb) => cb({ token: getAccessToken() || this.initialToken }),
      transports: ['websocket', 'polling'],
      reconnection: true,
      // Never give up: an agent's console must come back by itself after a
      // network blip, a laptop sleep or a server restart.
      reconnectionAttempts: Infinity,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: MAX_RECONNECT_DELAY_MS,
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.standingSubscriptions.forEach(({ event, data }) => this.socket?.emit(event, data));
      this.notifyConnectionChange(true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.notifyConnectionChange(false);
      // Dropped by the server (e.g. it refused an expired token). Socket.IO
      // does not retry this case on its own; retry with back-off, picking up
      // the refreshed token.
      if (reason === 'io server disconnect') this.scheduleServerDropRetry();
    });

    this.addWakeListeners();

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      this.reconnectAttempts++;
    });

    // Forward all events to registered handlers
    this.socket.onAny((event: string, data: any) => {
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.forEach((handler) => handler(data));
      }
    });
  }

  /**
   * One holder more of the shared connection; connects on the first. The
   * notification bridge is installed once here, not per component (it was
   * per component, so every notification was added once per mounted page).
   */
  acquire(token: string) {
    this.holders += 1;
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
    this.connect(token);
    if (!this.notificationBridge) {
      this.notificationBridge = this.subscribe('notification', relayNotification);
    }
  }

  /**
   * One holder fewer. The connection closes only when nobody holds it — and
   * after a short grace, because a page change unmounts one page's
   * components before mounting the next one's.
   */
  release() {
    this.holders = Math.max(0, this.holders - 1);
    if (this.holders > 0 || this.releaseTimer) return;
    this.releaseTimer = setTimeout(() => {
      this.releaseTimer = null;
      if (this.holders > 0) return;
      this.notificationBridge?.();
      this.notificationBridge = null;
      this.disconnect();
    }, RELEASE_GRACE_MS);
  }

  disconnect() {
    if (this.serverDropTimer) clearTimeout(this.serverDropTimer);
    this.serverDropTimer = null;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /** Reconnect now if not connected (tab shown again, network back). */
  reconnectNow() {
    if (this.socket && !this.socket.connected) this.socket.connect();
  }

  private scheduleServerDropRetry() {
    if (this.serverDropTimer) return;
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * 2 ** (this.reconnectAttempts - 1), MAX_RECONNECT_DELAY_MS);
    this.serverDropTimer = setTimeout(() => {
      this.serverDropTimer = null;
      this.reconnectNow();
    }, delay);
  }

  private addWakeListeners() {
    if (this.wakeListenersAdded || typeof window === 'undefined') return;
    this.wakeListenersAdded = true;
    window.addEventListener('online', () => this.reconnectNow());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.reconnectNow();
    });
  }

  subscribe<K extends keyof WebSocketEvents>(
    event: K,
    handler: EventHandler<WebSocketEvents[K]>
  ) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);

    return () => {
      this.eventHandlers.get(event)?.delete(handler);
    };
  }

  joinRoom(type: 'ticket' | 'inbox' | 'conversation' | 'tenant', id: string) {
    if (this.socket?.connected) {
      this.socket.emit('subscribe', { type, id });
    }
  }

  leaveRoom(type: 'ticket' | 'inbox' | 'conversation' | 'tenant', id: string) {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe', { type, id });
    }
  }

  /**
   * Fire-and-forget message to the server right now (dropped if disconnected).
   * For transient, non-standing signals like collision presence/typing (#18947)
   * where a missed frame simply means a slightly stale "also editing" banner —
   * unlike standing subscriptions, these must NOT be replayed on reconnect.
   */
  emitMessage(event: string, data: Record<string, unknown>) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Join a server-side room by message (e.g. subscribe_voice_queue), now if
   * connected and again on every (re)connect until removed.
   */
  addStandingSubscription(event: string, data: Record<string, unknown>) {
    const key = standingKey(event, data);
    const existing = this.standingSubscriptions.get(key);
    if (existing) {
      // Already in the room for another component: just count this holder.
      existing.holders += 1;
      return;
    }
    this.standingSubscriptions.set(key, { event, data, holders: 1 });
    if (this.socket?.connected) this.socket.emit(event, data);
  }

  /**
   * Release one holder of a room; the room is left (and no longer re-joined)
   * when the last holder goes. `unsubscribeEvent` (e.g. unsubscribe_voice_queue)
   * pairs with `subscribe_…` by name.
   */
  removeStandingSubscription(unsubscribeEvent: string, data: Record<string, unknown>) {
    const key = standingKey(unsubscribeEvent.replace(/^unsubscribe_/, 'subscribe_'), data);
    const existing = this.standingSubscriptions.get(key);
    if (existing && existing.holders > 1) {
      existing.holders -= 1;
      return;
    }
    this.standingSubscriptions.delete(key);
    if (this.socket?.connected) this.socket.emit(unsubscribeEvent, data);
  }
}

function standingKey(event: string, data: Record<string, unknown>): string {
  return `${event}:${JSON.stringify(data)}`;
}

/** Server notifications into the notification centre, and urgent ones as toasts. */
function relayNotification(notification: WebSocketEvents['notification']) {
  const mappedType = mapNotificationType(notification.type);

  // Build metadata from notification data
  const metadata = notification.data
    ? {
        ticketId: notification.data.ticketId,
        ticketNumber: notification.data.ticketNumber,
        severity: notification.data.severity as 'minor' | 'major' | 'critical' | undefined,
        breachType: notification.data.breachType,
        recipientRole: notification.data.recipientRole as 'assignee' | 'admin' | 'team_lead' | undefined,
        assigneeName: notification.data.assigneeName,
      }
    : undefined;

  useNotificationStore.getState().addNotification({
    id: notification.id,
    type: mappedType,
    title: notification.title,
    message: notification.message,
    link: notification.link,
    createdAt: notification.createdAt,
    read: false,
    metadata,
  });

  // Dispatch custom event for urgent notifications (toasts)
  if (notification.type === 'sla_breach' || mappedType === 'error' || mappedType === 'warning') {
    window.dispatchEvent(
      new CustomEvent('urgent-notification', {
        detail: {
          id: notification.id,
          type: mappedType,
          title: notification.title,
          message: notification.message,
          link: notification.link,
          originalType: notification.type,
          severity: notification.data?.severity,
        },
      })
    );
  }
}

// Singleton instance
export const webSocketService = new WebSocketService();

// React hook for WebSocket integration
export function useWebSocket() {
  const { token, isAuthenticated } = useAuthStore();
  const [isConnected, setIsConnected] = useState(webSocketService.isConnected());

  useEffect(() => {
    const unsubConnection = webSocketService.onConnectionChange(setIsConnected);
    return () => { unsubConnection(); };
  }, []);

  // Hold the one shared connection while this component is mounted and the
  // user is signed in. Components come and go with every page change; the
  // connection must not: it used to be torn down whenever any of them
  // unmounted, and the others never reopened it — the agent looked offline
  // ("Can't take calls") and missed calls offered in the gap.
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    webSocketService.acquire(token);
    return () => webSocketService.release();
  }, [isAuthenticated, token]);

  const subscribeToTicket = useCallback((ticketId: string) => {
    webSocketService.joinRoom('ticket', ticketId);
    return () => webSocketService.leaveRoom('ticket', ticketId);
  }, []);

  const subscribeToConversation = useCallback((conversationId: string) => {
    webSocketService.joinRoom('conversation', conversationId);
    return () => webSocketService.leaveRoom('conversation', conversationId);
  }, []);

  const onTicketCreated = useCallback(
    (handler: EventHandler<WebSocketEvents['ticket:created']>) => {
      return webSocketService.subscribe('ticket:created', handler);
    },
    []
  );

  const onTicketUpdated = useCallback(
    (handler: EventHandler<WebSocketEvents['ticket:updated']>) => {
      return webSocketService.subscribe('ticket:updated', handler);
    },
    []
  );

  const onTicketAssigned = useCallback(
    (handler: EventHandler<WebSocketEvents['ticket:assigned']>) => {
      return webSocketService.subscribe('ticket:assigned', handler);
    },
    []
  );

  const onNewMessage = useCallback(
    (handler: EventHandler<WebSocketEvents['message:new']>) => {
      return webSocketService.subscribe('message:new', handler);
    },
    []
  );

  const onSlaWarning = useCallback(
    (handler: EventHandler<WebSocketEvents['sla:warning']>) => {
      return webSocketService.subscribe('sla:warning', handler);
    },
    []
  );

  const onTyping = useCallback(
    (handler: EventHandler<WebSocketEvents['typing']>) => {
      return webSocketService.subscribe('typing', handler);
    },
    []
  );

  // Generic room subscribe/unsubscribe for voice and other channels. A
  // subscription made before the socket connects is sent once it does, and
  // is re-sent after a reconnect.
  const subscribe = useCallback((event: string, data: Record<string, unknown>) => {
    webSocketService.addStandingSubscription(event, data);
  }, []);

  const unsubscribe = useCallback((event: string, data: Record<string, unknown>) => {
    webSocketService.removeStandingSubscription(event, data);
  }, []);

  // One-shot emit for transient signals (e.g. collision presence/typing).
  const emit = useCallback((event: string, data: Record<string, unknown>) => {
    webSocketService.emitMessage(event, data);
  }, []);

  // Generic event handlers for voice events
  const on = useCallback((event: string, handler: EventHandler<any>) => {
    if (!webSocketService['eventHandlers'].has(event)) {
      webSocketService['eventHandlers'].set(event, new Set());
    }
    webSocketService['eventHandlers'].get(event)!.add(handler);
  }, []);

  const off = useCallback((event: string, handler: EventHandler<any>) => {
    webSocketService['eventHandlers'].get(event)?.delete(handler);
  }, []);

  return {
    isConnected,
    subscribeToTicket,
    subscribeToConversation,
    onTicketCreated,
    onTicketUpdated,
    onTicketAssigned,
    onNewMessage,
    onSlaWarning,
    onTyping,
    // Generic methods for voice and other channels
    subscribe,
    unsubscribe,
    emit,
    on,
    off,
  };
}
